import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import styles from '../assets/emptyPropertyTip.module.css';

const EmptyPropertyTip = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    address_1: '',
    address_2: '',
    address_3: '',
    town: '',
    postcode: '',
    description: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (
      !formData.address_1 ||
      !formData.town ||
      !formData.postcode ||
      !formData.description
    ) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.post(
        '/guardians/empty-property-tip',
        formData
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setSubmitted(true);
      } else {
        toast.error(
          response.data.message || 'Failed to submit empty property tip'
        );
      }
    } catch (error) {
      console.error('Error submitting empty property tip:', error);
      const errorMessage =
        error.response?.data?.message ||
        'Failed to submit empty property tip. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Show success content after submission
  if (submitted) {
    return (
      <div className={styles.emptyPropertyTip}>
        <div className={styles.container}>
          {/* Green Label */}
          <div className={styles.greenLabel}>Empty Property Tip</div>

          {/* Success Content */}
          <div className={styles.successContent}>
            <h1 className={styles.successTitle}>
              Thank you for providing details of an empty property!
            </h1>
            <h2 className={styles.successSubtitle}>
              Our team will start investigating this lead
            </h2>
            <div className={styles.successDescription}>
              Thank you for informing us about the empty property you've seen.
              Your contribution plays a crucial role in our mission to transform
              vacant spaces into vibrant communities for our guardians. We
              appreciate your commitment to helping us create safe, secure, and
              sustainable living environments. Our team will review the
              information provided and take appropriate action to assess the
              property's potential. Together, we can make a positive impact on
              communities by revitalising empty properties and providing
              much-needed housing solutions. Thank you for being part of our
              journey!
            </div>
            <div className={styles.successActions}>
              <button
                onClick={() => navigate('/guardian-dashboard')}
                className={styles.returnButton}
              >
                Return To Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show form content
  return (
    <div className={styles.emptyPropertyTip}>
      <div className={styles.container}>
        {/* Green Label */}
        <div className={styles.greenLabel}>Empty Property Tip</div>

        {/* Main Heading */}
        <div className={styles.mainHeading}>
          <h1>Empty Property Tip</h1>
          <h2>Earn money for spotting empty buildings!</h2>
        </div>

        {/* Description */}
        <div className={styles.description}>
          Have you seen an empty property that you feel can be occupied by
          Property Guardians? Let us know the details and we will get in contact
          with the owners. If we take on the building, you may be in for a
          monetary reward!
        </div>

        {/* Form Section */}
        <div className={styles.formSection}>
          <div className={styles.formBox}>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="address_1" className={styles.label}>
                    Address 1 <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    id="address_1"
                    name="address_1"
                    value={formData.address_1}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="address_2" className={styles.label}>
                    Address 2
                  </label>
                  <input
                    type="text"
                    id="address_2"
                    name="address_2"
                    value={formData.address_2}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="address_3" className={styles.label}>
                    Address 3
                  </label>
                  <input
                    type="text"
                    id="address_3"
                    name="address_3"
                    value={formData.address_3}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="town" className={styles.label}>
                    Town <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    id="town"
                    name="town"
                    value={formData.town}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="postcode" className={styles.label}>
                    Postcode <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    id="postcode"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="description" className={styles.label}>
                    Description <span className={styles.required}>*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className={styles.textarea}
                    required
                    rows="5"
                  />
                </div>
              </div>

              <div className={styles.formActions}>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Send empty property tip'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmptyPropertyTip;
